import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../App';
import { Loan } from '../types';
import api from '../api';
import NavBar from '../components/NavBar';

const PIPELINE_STEPS = [
  { key: '01-Loan Modeling', label: '01 Modeling', short: '01' },
  { key: '02-Initial Underwriting', label: '02 Initial UW', short: '02' },
  { key: '03-Underwriting', label: '03 Underwriting', short: '03' },
  { key: '04-Approval', label: '04 Approval', short: '04' },
  { key: '05-Closing', label: '05 Closing', short: '05' },
  { key: '06-Boarded', label: '06 Boarded', short: '06' },
  { key: '07-Servicing', label: '07 Servicing', short: '07' },
];

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  '01-Loan Modeling': { label: '01 Modeling', color: 'bg-gray-700 text-gray-300' },
  '02-Initial Underwriting': { label: '02 Initial UW', color: 'bg-[#7C3AED]/20 text-[#A78BFA]' },
  '03-Underwriting': { label: '03 Underwriting', color: 'bg-[#2563EB]/20 text-[#60A5FA]' },
  '04-Approval': { label: '04 Approval', color: 'bg-[#0891B2]/20 text-[#22D3EE]' },
  '05-Closing': { label: '05 Closing', color: 'bg-[#D97706]/20 text-[#FCD34D]' },
  '06-Boarded': { label: '06 Boarded', color: 'bg-[#059669]/20 text-[#34D399]' },
  '07-Servicing': { label: '07 Servicing', color: 'bg-[#10B981]/20 text-[#6EE7B7]' },
  declined: { label: 'Declined', color: 'bg-[#EF4444]/20 text-[#F87171]' },
};

function ExceptionCount({ rulesResult }: { rulesResult: any }) {
  const exceptions = rulesResult?.exceptions || [];
  const critical = exceptions.filter((e: any) => e.severity === 'CRITICAL').length;
  const high = exceptions.filter((e: any) => e.severity === 'HIGH').length;

  if (exceptions.length === 0) {
    return <span className="text-xs text-[#10B981]">✓ Clean</span>;
  }

  return (
    <div className="flex gap-1">
      {critical > 0 && (
        <span className="text-xs bg-[#EF4444]/20 text-[#F87171] px-1.5 py-0.5 rounded font-mono">
          {critical} CRIT
        </span>
      )}
      {high > 0 && (
        <span className="text-xs bg-[#F59E0B]/20 text-[#FBBF24] px-1.5 py-0.5 rounded font-mono">
          {high} HIGH
        </span>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/loans')
      .then(res => setLoans(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const fmtMoney = (n: number | undefined) =>
    n != null ? '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '—';

  const activePipelineCount = loans.filter(l => l.status !== 'declined').length;
  const declinedCount = loans.filter(l => l.status === 'declined').length;

  return (
    <div className="min-h-screen bg-[#0F1117]">
      <NavBar />

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white">
              {user?.role === 'admin' ? 'All Loans' : 'My Pipeline'}
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">
              {user?.role === 'admin'
                ? 'Review and manage all loan applications'
                : 'Manage your loan applications'}
            </p>
          </div>
          <div className="flex gap-3">
            {user?.role === 'admin' && (
              <Link
                to="/admin/rules"
                className="bg-[#1A1D27] hover:bg-[#252836] border border-gray-700 hover:border-[#2563EB] text-gray-300 hover:text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                ⚙ Rule Editor
              </Link>
            )}
            <Link
              to="/loans/new"
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <span>+</span> New Loan
            </Link>
          </div>
        </div>

        {!loading && (
          <>
            {/* Pipeline Status Bar */}
            <div className="bg-[#1A1D27] rounded-xl border border-gray-800 p-4 mb-4">
              <div className="flex items-center gap-1 mb-3">
                <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">Pipeline</span>
                <span className="ml-auto text-xs text-gray-600">{activePipelineCount} active · {declinedCount} declined</span>
              </div>
              <div className="flex gap-1">
                {PIPELINE_STEPS.map((step, idx) => {
                  const count = loans.filter(l => l.status === step.key).length;
                  const isLast = idx === PIPELINE_STEPS.length - 1;
                  return (
                    <React.Fragment key={step.key}>
                      <div className="flex-1 text-center">
                        <div className={`rounded py-2 px-1 ${count > 0 ? 'bg-[#2563EB]/20 border border-[#2563EB]/40' : 'bg-[#0F1117] border border-gray-800'}`}>
                          <div className={`text-lg font-bold font-mono ${count > 0 ? 'text-[#60A5FA]' : 'text-gray-700'}`}>{count}</div>
                          <div className={`text-[10px] mt-0.5 hidden sm:block truncate ${count > 0 ? 'text-[#93C5FD]' : 'text-gray-600'}`}>{step.label}</div>
                        </div>
                      </div>
                      {!isLast && (
                        <div className="flex items-center text-gray-700 text-xs">›</div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Loan Table */}
            <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden">
              {loans.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-gray-500 mb-4">No loans yet</div>
                  <Link
                    to="/loans/new"
                    className="text-[#2563EB] hover:text-[#60A5FA] text-sm underline"
                  >
                    Create your first loan application
                  </Link>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Sponsor / Product</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">State</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Loan Amt</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Tier</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Exceptions</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                      {user?.role === 'admin' && (
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Originator</th>
                      )}
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {loans.map(loan => {
                      const statusCfg = STATUS_CONFIG[loan.status] || { label: loan.status, color: 'bg-gray-700 text-gray-400' };
                      const rulesResult = loan.rulesResult as any;
                      const displayName = loan.sponsorName || loan.entityName || '—';
                      const displayProduct = loan.productType || loan.loanProgram || '—';
                      return (
                        <tr
                          key={loan.id}
                          onClick={() => navigate(`/loans/${loan.id}`)}
                          className="hover:bg-[#252836] cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="text-sm text-white font-medium">{displayName}</div>
                            <div className="text-xs text-gray-500">{displayProduct}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm text-gray-300">{loan.state || '—'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm text-white">{fmtMoney(loan.loanAmount)}</span>
                          </td>
                          <td className="px-4 py-3">
                            {rulesResult?.borrowerTier ? (
                              <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${rulesResult.borrowerTier === 'Tier1'
                                ? 'bg-[#10B981]/20 text-[#34D399]'
                                : rulesResult.borrowerTier === 'Tier2'
                                  ? 'bg-[#F59E0B]/20 text-[#FBBF24]'
                                  : 'bg-[#EF4444]/20 text-[#F87171]'
                                }`}>
                                {rulesResult.borrowerTier}
                              </span>
                            ) : (
                              <span className="text-gray-600 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <ExceptionCount rulesResult={rulesResult} />
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${statusCfg.color}`}>
                              {statusCfg.label}
                            </span>
                          </td>
                          {user?.role === 'admin' && (
                            <td className="px-4 py-3">
                              <span className="text-xs text-gray-400">{loan.user?.name}</span>
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-500">
                              {new Date(loan.createdAt).toLocaleDateString()}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {loading && (
          <div className="bg-[#1A1D27] rounded-xl border border-gray-800 p-12 text-center">
            <div className="text-gray-500">Loading loans...</div>
          </div>
        )}
      </div>
    </div>
  );
}
